import React from "react";
import { ProjectActions, TeamActions } from "@domain/schemas/workspace";
import type { ProjectAction, TeamAction } from "@domain/schemas/workspace";
import { useProjectPermission, useTeamPermission } from "@presentation/components/helpers/usePermissionCheck";
import { PermissionDeniedView } from "@presentation/components/permission/PermissionDeniedView";
import { FullscreenLoader } from "@presentation/components/notifications";

export type PermissionGateRequirement = { scope: "team"; action: TeamAction } | { scope: "project"; action: ProjectAction };

interface PermissionGateProps {
  requirement: PermissionGateRequirement;
  teamId: string | null | undefined;
  projectId: string | null | undefined;
  children: React.ReactNode;
}

/**
 * Gates `children` behind a team or project action, rendering a loader while unresolved and
 * entitlement-locked vs permission-denied states distinctly once resolved - never a silent
 * redirect, so the user always knows why. Usable for a whole route or a single control.
 *
 * Both permission hooks are called unconditionally (rules of hooks): whichever scope isn't the
 * active one is given a `null` id, which short-circuits that hook to an unresolved, non-fetching
 * answer.
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({ requirement, teamId, projectId, children }) => {
  const teamResult = useTeamPermission(requirement.scope === "team" ? teamId : null, requirement.scope === "team" ? requirement.action : TeamActions.ViewInventory);
  const projectResult = useProjectPermission(requirement.scope === "project" ? projectId : null, requirement.scope === "project" ? requirement.action : ProjectActions.View);

  const { allowed, reason } = requirement.scope === "team" ? teamResult : projectResult;

  if (allowed) return <>{children}</>;
  return reason === null ? <FullscreenLoader /> : <PermissionDeniedView reason={reason} scopeLabel={requirement.scope} />;
};
